import { Persona } from "@/features/agent-registry/model/interfaces";
import { Credential } from "./connectors/credentials";
import { Connector } from "./connectors/connectors";
import { ConnectorCredentialPairStatus } from "@/features/admin/connector/[ccPairId]/types";

export interface UserSpecificAgentPreference {
  disabled_tool_ids?: number[];
}

export type UserSpecificAgentPreferences = Record<
  number,
  UserSpecificAgentPreference
>;

export enum ThemePreference {
  LIGHT = "light",
  DARK = "dark",
  SYSTEM = "system",
}

interface UserPreferences {
  // TODO: rename to agent — 
  chosen_assistants: number[] | null;
  visible_assistants: number[];
  hidden_assistants: number[];
  pinned_assistants?: number[];
  default_model: string | null;
  recent_assistants: number[];
  auto_scroll: boolean;
  shortcut_enabled: boolean;
  temperature_override_enabled: boolean;
  theme_preference: ThemePreference | null;
  chat_background: string | null;
  default_app_mode: "AUTO" | "CHAT" | "SEARCH";
  // Voice preferences
  voice_auto_send?: boolean;
  voice_auto_playback?: boolean;
  voice_playback_speed?: number;
}

export interface MemoryItem {
  id: number | null;
  content: string;
}

export interface UserPersonalization {
  name: string;
  role: string;
  memories: MemoryItem[];
  use_memories: boolean;
  enable_memory_tool: boolean;
  user_preferences: string;
}

export enum AccountType {
  STANDARD = "STANDARD",
  BOT = "BOT",
  EXT_PERM_USER = "EXT_PERM_USER",
  SERVICE_ACCOUNT = "SERVICE_ACCOUNT",
  ANONYMOUS = "ANONYMOUS",
}

export enum UserRole {
  LIMITED = "limited",
  BASIC = "basic",
  ADMIN = "admin",
  CURATOR = "curator",
  GLOBAL_CURATOR = "global_curator",
  EXT_PERM_USER = "ext_perm_user",
  SLACK_USER = "slack_user",
}

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.BASIC]: "Basic",
  [UserRole.ADMIN]: "Admin",
  [UserRole.GLOBAL_CURATOR]: "Global Curator",
  [UserRole.CURATOR]: "Curator",
  [UserRole.LIMITED]: "Limited",
  [UserRole.EXT_PERM_USER]: "External Permissioned User",
  [UserRole.SLACK_USER]: "Slack User",
};

export enum UserStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  INVITED = "invited",
  REQUESTED = "requested",
}

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: "Active",
  [UserStatus.INACTIVE]: "Inactive",
  [UserStatus.INVITED]: "Invite Pending",
  [UserStatus.REQUESTED]: "Request to Join",
};

export const INVALID_ROLE_HOVER_TEXT: Partial<Record<UserRole, string>> = {
  [UserRole.BASIC]: "Basic users can't perform any admin actions",
  [UserRole.ADMIN]: "Admin users can perform all admin actions",
  [UserRole.GLOBAL_CURATOR]:
    "Global Curator users can perform admin actions for all groups they are a member of",
  [UserRole.CURATOR]: "Curator role must be assigned in the Groups tab",
  [UserRole.SLACK_USER]:
    "This role is automatically assigned to users who only use VNP Workplace via Slack",
};

export interface User {
  id: string;
  workspace_id: string; // NEW — CR-002. Backend đã trả field này ở GET /api/me (auth_handler.go:200), chỉ chưa khai báo type.
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  role: UserRole;
  preferences: UserPreferences;
  current_token_created_at?: Date;
  current_token_expiry_length?: number;
  oidc_expiry?: Date;
  is_cloud_superuser?: boolean;
  team_name: string | null;
  is_anonymous_user?: boolean;
  // If user does not have a configured password
  // (i.e.) they are using an oauth flow
  // or are in a no-auth situation
  // we don't want to show them things like the reset password
  // functionality
  password_configured?: boolean;
  tenant_info?: TenantInfo | null;
  personalization?: UserPersonalization;
}

export interface TenantInfo {
  new_tenant?: NewTenantInfo | null;
  invitation?: NewTenantInfo | null;
}

export interface NewTenantInfo {
  tenant_id: string;
  number_of_users: number;
}

export interface AllUsersResponse {
  accepted: User[];
  invited: User[];
  slack_users: User[];
  accepted_pages: number;
  invited_pages: number;
  slack_users_pages: number;
}

export interface AcceptedUserSnapshot {
  id: string;
  email: string;
  role: UserRole;
  is_active: boolean;
}

export interface InvitedUserSnapshot {
  email: string;
}

export interface MinimalUserSnapshot {
  id: string;
  email: string;
}

export type ValidInputTypes =
  | "load_state"
  | "poll"
  | "event"
  | "slim_retrieval";
export type ValidStatuses =
  | "invalid"
  | "success"
  | "completed_with_errors"
  | "canceled"
  | "failed"
  | "in_progress"
  | "not_started";
export type TaskStatus = "PENDING" | "STARTED" | "SUCCESS" | "FAILURE";
export type Feedback = "like" | "dislike" | "mixed";
export type AccessType = "public" | "private" | "sync";
export type ProcessingMode = "REGULAR" | "FILE_SYSTEM";
export type SessionType = "Chat" | "Search" | "Slack";

export interface DocumentBoostStatus {
  document_id: string;
  semantic_id: string;
  link: string;
  boost: number;
  hidden: boolean;
}

export interface FailedConnectorIndexingStatus {
  cc_pair_id: number;
  name: string;
  error_msg: string | null;
  is_deletable: boolean;
  connector_id: number;
  credential_id: number;
}

export interface IndexAttemptSnapshot {
  id: number;
  status: ValidStatuses | null;
  from_beginning: boolean;
  new_docs_indexed: number;
  docs_removed_from_index: number;
  total_docs_indexed: number;
  error_msg: string | null;
  error_count: number;
  full_exception_trace: string | null;
  time_started: string | null;
  time_updated: string;
}

export interface ConnectorStatus<ConnectorConfigType, ConnectorCredentialType> {
  cc_pair_id: number;
  name: string;
  connector: Connector<ConnectorConfigType>;
  credential: Credential<ConnectorCredentialType>;
  access_type: AccessType;
  groups: number[];
}

export interface ConnectorIndexingStatus<
  ConnectorConfigType,
  ConnectorCredentialType,
> extends ConnectorStatus<ConnectorConfigType, ConnectorCredentialType> {
  // Inlcude data only necessary for indexing statuses in admin page
  last_success: string | null;
  last_status: ValidStatuses | null;
  last_finished_status: ValidStatuses | null;
  cc_pair_status: ConnectorCredentialPairStatus;
  in_repeated_error_state: boolean;
  latest_index_attempt: IndexAttemptSnapshot | null;
  docs_indexed: number;
}

export interface ConnectorIndexingStatusLite {
  cc_pair_id: number;
  name: string;
  source: ValidSources;
  access_type: AccessType;
  in_progress: boolean;
  cc_pair_status: ConnectorCredentialPairStatus;
  last_finished_status: ValidStatuses | null;
  last_status: ValidStatuses | null;
  last_success: string | null;
  is_editable: boolean;
  docs_indexed: number;
  in_repeated_error_state: boolean;
  latest_index_attempt_docs_indexed: number | null;
}

export interface FederatedConnectorStatus {
  id: number;
  source: ValidSources;
  name: string;
}

export interface SourceSummary {
  total_connectors: number;
  active_connectors: number;
  public_connectors: number;
  total_docs_indexed: number;
}

export interface ConnectorIndexingStatusLiteResponse {
  source: ValidSources;
  summary: SourceSummary;
  current_page: number;
  total_pages: number;
  indexing_statuses: (ConnectorIndexingStatusLite | FederatedConnectorStatus)[];
}

export interface FederatedConnectorDetail {
  id: number;
  source: ValidSources.FederatedSlack;
  name: string;
  credentials: Record<string, any>;
  config: Record<string, any>;
  oauth_token_exists: boolean;
  oauth_token_expires_at: string | null;
  document_sets: Array<{
    id: number;
    name: string;
    entities: Record<string, any>;
  }>;
}

export interface OAuthPrepareAuthorizationResponse {
  url: string;
}

export interface OAuthBaseCallbackResponse {
  success: boolean;
  message: string;
  finalize_url: string | null;
  redirect_on_success: string;
}

export interface OAuthSlackCallbackResponse extends OAuthBaseCallbackResponse {
  team_id: string;
  authed_user_id: string;
}

export interface ConfluenceAccessibleResource {
  id: string;
  name: string;
  url: string;
  scopes: string[];
  avatarUrl: string;
}

export interface OAuthConfluencePrepareFinalizationResponse {
  success: boolean;
  message: string;
  accessible_resources: ConfluenceAccessibleResource[];
}

export interface OAuthConfluenceFinalizeResponse {
  success: boolean;
  message: string;
  redirect_url: string;
}

export interface CCPairBasicInfo {
  has_successful_run: boolean;
  source: ValidSources;
  status: ConnectorCredentialPairStatus;
}

export type ConnectorSummary = {
  count: number;
  active: number;
  public: number;
  totalDocsIndexed: number;
  errors: number; // New field for error count
};

export type GroupedConnectorSummaries = Record<ValidSources, ConnectorSummary>;

// DELETION

export interface DeletionAttemptSnapshot {
  connector_id: number;
  credential_id: number;
  status: TaskStatus;
}

// DOCUMENT SETS
export interface CCPairDescriptor<ConnectorType, CredentialType> {
  id: number;
  name: string;
  connector: Connector<ConnectorType>;
  credential: Credential<CredentialType>;
  access_type: AccessType;
}

export interface FederatedConnectorConfig {
  federated_connector_id: number;
  entities: Record<string, any>;
}

export interface FederatedConnectorDescriptor {
  id: number;
  name: string;
  source: string;
  entities: Record<string, any>;
}

// Simplified interfaces with minimal data
export interface CCPairSummary {
  id: number;
  name: string;
  source: ValidSources;
  access_type: AccessType;
}

export interface FederatedConnectorSummary {
  id: number;
  name: string;
  source: string;
  entities: Record<string, any>;
}

export interface DocumentSetSummary {
  // TASK-AB003-002: real ID is knowledge.document_set.id, VARCHAR(50) e.g.
  // "ds_...". Was `number` because the sync-service stub this used to hit
  // always faked ID:1 — becomes a real string once TASK-AB003-001 (backend)
  // repoints document-set routes to knowledge-service.
  id: string;
  name: string;
  description: string;
  cc_pair_summaries: CCPairSummary[];
  is_up_to_date: boolean;
  is_public: boolean;
  users: string[];
  groups: number[];
  federated_connector_summaries: FederatedConnectorSummary[];
}

export interface Tag {
  tag_key: string;
  tag_value: string;
  source: ValidSources;
}

// STANDARD ANSWERS
export interface StandardAnswerCategory {
  id: number;
  name: string;
}

export interface StandardAnswer {
  id: number;
  keyword: string;
  answer: string;
  match_regex: boolean;
  match_any_keywords: boolean;
  categories: StandardAnswerCategory[];
}

// SLACK BOT CONFIGS

export type AnswerFilterOption =
  | "well_answered_postfilter"
  | "questionmark_prefilter";

export interface ChannelConfig {
  channel_name: string;
  respond_tag_only?: boolean;
  respond_to_bots?: boolean;
  is_ephemeral?: boolean;
  show_continue_in_web_ui?: boolean;
  respond_member_group_list?: string[];
  answer_filters?: AnswerFilterOption[];
  follow_up_tags?: string[];
  disabled?: boolean;
}

export interface SlackChannelDescriptor {
  id: string;
  name: string;
}

/* EE Only Types */
export interface UserGroup {
  id: number;
  name: string;
  users: User[];
  curator_ids: string[];
  cc_pairs: CCPairDescriptor<any, any>[];
  /** Real sync.connector IDs shared with this group — see GroupsPage's
   * SharedGroupResources. Always [] for legacy cc_pairs above, whose shape
   * (numeric id, nested connector/credential) doesn't match sync.connector. */
  connector_ids?: string[];
  document_sets: DocumentSetSummary[];
  personas: Persona[];
  is_up_to_date: boolean;
  is_up_for_deletion: boolean;
  is_default: boolean;
}

export enum ValidSources {
  Web = "web",
  GitHub = "github",
  GitLab = "gitlab",
  Slack = "slack",
  GoogleDrive = "google_drive",
  Gmail = "gmail",
  Bookstack = "bookstack",
  Outline = "outline",
  Confluence = "confluence",
  Jira = "jira",
  Productboard = "productboard",
  Slab = "slab",
  Coda = "coda",
  Notion = "notion",
  Guru = "guru",
  Gong = "gong",
  Zulip = "zulip",
  Linear = "linear",
  Hubspot = "hubspot",
  Document360 = "document360",
  File = "file",
  UserFile = "user_file",
  GoogleSites = "google_sites",
  Loopio = "loopio",
  Dropbox = "dropbox",
  Discord = "discord",
  Salesforce = "salesforce",
  Sharepoint = "sharepoint",
  Teams = "teams",
  Microsoft365 = "microsoft365",
  Zendesk = "zendesk",
  Discourse = "discourse",
  Axero = "axero",
  Clickup = "clickup",
  Wikipedia = "wikipedia",
  Mediawiki = "mediawiki",
  Asana = "asana",
  S3 = "s3",
  R2 = "r2",
  GoogleCloudStorage = "google_cloud_storage",
  Xenforo = "xenforo",
  OciStorage = "oci_storage",
  NotApplicable = "not_applicable",
  IngestionApi = "ingestion_api",
  Freshdesk = "freshdesk",
  Fireflies = "fireflies",
  Egnyte = "egnyte",
  Airtable = "airtable",
  Gitbook = "gitbook",
  Highspot = "highspot",
  DrupalWiki = "drupal_wiki",
  Imap = "imap",
  Bitbucket = "bitbucket",
  TestRail = "testrail",

  // Craft-specific sources
  CraftFile = "craft_file",

  // Federated Connectors
  FederatedSlack = "federated_slack",
}

export const federatedSourceToRegularSource = (
  maybeFederatedSource: ValidSources
): ValidSources => {
  if (maybeFederatedSource === ValidSources.FederatedSlack) {
    return ValidSources.Slack;
  }
  return maybeFederatedSource;
};

export const validAutoSyncSources = [
  ValidSources.Confluence,
  ValidSources.Jira,
  ValidSources.GoogleDrive,
  ValidSources.Gmail,
  ValidSources.Slack,
  ValidSources.Salesforce,
  ValidSources.GitHub,
  ValidSources.Sharepoint,
  ValidSources.Teams,
] as const;

// Create a type from the array elements
export type ValidAutoSyncSource = (typeof validAutoSyncSources)[number];

export type ConfigurableSources = Exclude<
  ValidSources,
  | ValidSources.NotApplicable
  | ValidSources.IngestionApi
  | ValidSources.FederatedSlack // is part of ValiedSources.Slack
  | ValidSources.UserFile
  | ValidSources.CraftFile // User Library - managed through dedicated UI
>;

export const oauthSupportedSources: ConfigurableSources[] = [
  ValidSources.Slack,
  // NOTE: temporarily disabled until our GDrive App is approved
  // ValidSources.GoogleDrive,
  ValidSources.Confluence,
];

export type OAuthSupportedSource = (typeof oauthSupportedSources)[number];

// Federated Connector Types
export interface CredentialFieldSpec {
  type: string;
  description: string;
  required: boolean;
  default?: any;
  example?: any;
  secret: boolean;
}

export interface ConfigurationFieldSpec {
  type: string;
  description: string;
  required: boolean;
  default?: any;
  example?: any;
  secret: boolean;
  hidden_when?: Record<string, any>;
}

export interface CredentialSchemaResponse {
  credentials: Record<string, CredentialFieldSpec>;
}

export interface ConfigurationSchemaResponse {
  configuration: Record<string, ConfigurationFieldSpec>;
}

export interface FederatedConnectorCreateRequest {
  source: string;
  credentials: Record<string, any>;
  config?: Record<string, any>;
}

export interface FederatedConnectorCreateResponse {
  id: number;
  source: string;
}

export interface IndexingStatusRequest {
  secondary_index?: boolean;
  access_type_filters?: string[];
  last_status_filters?: string[];
  docs_count_operator?: ">" | "<" | "=" | null;
  docs_count_value?: number | null;
  source_to_page?: Record<ValidSources, number>;
  source?: ValidSources;
  get_all_connectors?: boolean;
}
